import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { LoadingSpinner, PageError } from '@/components/ui';
import type { PendingTask } from '@haccp/api-client';

export default function TasksPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();

  const TYPE_CONFIG = {
    TEMPERATURE: { icon: '🌡️', label: t('tasks.typeTemperature'), color: 'border-l-blue-400' },
    CLEANING: { icon: '🧹', label: t('tasks.typeCleaning'), color: 'border-l-green-400' },
    OIL: { icon: '🍟', label: t('tasks.typeOil'), color: 'border-l-yellow-400' },
  } as const;

  const { data: tasks = [], isLoading, error } = useQuery({
    queryKey: ['tasks-pending'],
    queryFn: () => apiClient.getPendingTasks(),
    refetchInterval: 30_000,
  });

  const overdueTasks = tasks.filter((t) => t.status === 'OVERDUE');
  const pendingTasks = tasks.filter((t) => t.status === 'PENDING');

  const overdueByType = groupBy(overdueTasks, (t: PendingTask) => t.type);
  const pendingByType = groupBy(pendingTasks, (t: PendingTask) => t.type);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <span className="text-2xl">📋</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('tasks.title')}</h1>
              <p className="text-gray-600 mt-1">
                {tasks.length === 0
                  ? t('tasks.allUpToDate')
                  : t('tasks.pendingCount', { count: tasks.length })}
              </p>
            </div>
          </div>

          {/* Summary counters */}
          {!isLoading && tasks.length > 0 && (
            <div className="flex gap-3 mt-4 flex-wrap">
              {overdueTasks.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  <span className="text-red-700 text-sm font-medium">
                    {t('tasks.overdueCount', { count: overdueTasks.length })}
                  </span>
                </div>
              )}
              {pendingTasks.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span className="text-orange-700 text-sm font-medium">
                    {t('tasks.todoCount', { count: pendingTasks.length })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <LoadingSpinner message={t('tasks.loading')} />
        ) : error ? (
          <PageError message={t('tasks.loadError')} />
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <span className="text-5xl block mb-4">✅</span>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('tasks.done')}</h2>
            <p className="text-gray-500">{t('tasks.noTasks')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overdue section */}
            {overdueTasks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                  <h2 className="text-lg font-semibold text-red-700">{t('tasks.overdue')}</h2>
                  <span className="text-sm text-red-500 font-medium">({overdueTasks.length})</span>
                </div>
                <div className="space-y-4">
                  {Object.entries(overdueByType).map(([type, items]) => (
                    <TaskGroup key={type} type={type as PendingTask['type']} tasks={items} typeConfig={TYPE_CONFIG} t={t} />
                  ))}
                </div>
              </div>
            )}

            {/* Pending section */}
            {pendingTasks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                  <h2 className="text-lg font-semibold text-orange-700">{t('tasks.todo')}</h2>
                  <span className="text-sm text-orange-500 font-medium">({pendingTasks.length})</span>
                </div>
                <div className="space-y-4">
                  {Object.entries(pendingByType).map(([type, items]) => (
                    <TaskGroup key={type} type={type as PendingTask['type']} tasks={items} typeConfig={TYPE_CONFIG} t={t} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type TypeConfigMap = Record<string, { icon: string; label: string; color: string }>;

function TaskGroup({
  type,
  tasks,
  typeConfig,
  t,
}: {
  type: PendingTask['type'];
  tasks: PendingTask[];
  typeConfig: TypeConfigMap;
  t: (key: string, opts?: any) => string;
}) {
  const config = typeConfig[type as keyof typeof typeConfig];

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <span className="text-lg">{config.icon}</span>
        <span className="font-medium text-gray-700">{config.label}</span>
        <span className="text-xs text-gray-400 font-medium">({tasks.length})</span>
      </div>
      <div className="divide-y divide-gray-100">
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} typeConfig={typeConfig} t={t} />
        ))}
      </div>
    </div>
  );
}

function TaskRow({
  task,
  typeConfig,
  t,
}: {
  task: PendingTask;
  typeConfig: TypeConfigMap;
  t: (key: string, opts?: any) => string;
}) {
  const config = typeConfig[task.type as keyof typeof typeConfig];
  const isOverdue = task.status === 'OVERDUE';

  return (
    <a
      href={task.link}
      className={`flex items-center justify-between p-4 hover:bg-gray-50 transition border-l-4 ${
        isOverdue ? 'border-l-red-400 bg-red-50/30' : config.color
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{task.title}</span>
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
            isOverdue ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
          }`}>
            {isOverdue ? t('tasks.overdue') : t('tasks.todo')}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-0.5">{task.subtitle}</p>
        {task.dueInfo && (
          <p className={`text-xs mt-1 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`}>
            {task.dueInfo}
          </p>
        )}
      </div>
      <span className="text-gray-400 ml-3 shrink-0">→</span>
    </a>
  );
}

function groupBy<T>(arr: T[], fn: (item: T) => string): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  for (const item of arr) {
    const key = fn(item);
    if (!result[key]) result[key] = [];
    result[key].push(item);
  }
  return result;
}
